/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable indent */
import React, { useRef } from 'react';
import { connect } from 'react-redux';
import { useLocation, useParams } from 'react-router';
import _ from 'lodash';
import i18n from 'i18n';
import {
  Checkbox,
  CircularProgress,
  Grid,
  makeStyles,
  Tooltip,
  Typography,
} from '@material-ui/core';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { TimerOff } from '@material-ui/icons';
import palette from 'theme/palette';
import { isClientOperator } from 'utils/role';
import { useTableFilters } from 'hooks/useTableFilters';
import Button from 'components/Button';
import ButtonGroup from 'components/ButtonGroup';
import RefreshButton from 'components/RefreshButton';
import TableItems, {
  MenuActionItem,
  MenuActions,
  TableCell,
  TableRow,
} from 'components/TableItems';
import Permission from 'components/ValidPermission';
import { SearchBar } from 'components/SearchBar';
import FormSimulateApp from 'containers/Clients/ApplicationsClients/FormSimulateApp';
import {
  activateInstanceRequest,
  connectAsRequest,
  getAllInstancesRequest,
  getEnvironmentRequest,
  rebootInstanceRequest,
  removeInstanceRequest,
  terminateInstanceRequest,
  terminateMultipleInstancesRequest,
  toggleBlockInstancesRequest,
  toggleBlockMultipleInstancesRequest,
  unlinkInstanceRequest,
  validateZeroTrustRequest,
} from 'modules/environments/actions';
import { modalClose, modalOpen } from 'modules/modal/actions';
import { PermissionsData, RolesEnum } from 'modules/authenticate/interfaces';
import {
  EnvironmentFields,
  InstanceFields,
  ListInstanceFieldsDataProps,
} from 'modules/environments/interfaces';
import { ModalFields } from 'modules/modal/interfaces';
import ApplicationState from 'store/interfaces';
import { TableCellHeaderProps } from 'components/TableItems/interfaces';
import { useCheckboxes } from 'hooks';
import PercentIndicator from 'components/PercentIndicator';
import FormConnectAs from '../FormConnectAs';
import FormRegisterInstance from '../FormRegisterInstance';
import EnvironmentsTemplate from '../Templates/Environments';
import StatusInstance from './status';
import ValidateZeroTrust from './ValidateZeroTrust';
import InstanceDetails from './InstanceDetails';
import styles, {
  overrideModalSimulate,
  overrideModalTestZeroTrust,
} from './styles';
import {
  environmentStatus,
  getInitializingInstancesMessage,
  getMenuActionItems,
  getMonitorCellStyle,
  isRefreshStatusReboot,
  InstanceDetailsTooltip,
  ZeroTrustIcon,
  BenchmarkIcon,
} from './helpers';

const useStyles = makeStyles(styles);

const useQuery = () => {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
};

const initialQuery = {
  offset: 0,
  limit: 25,
  search: '',
};

const requiredActionsPermissions: Array<keyof PermissionsData> = [
  'can_block_environment_instances',
  'can_connect_instance',
  'can_restart_environment_instances',
  'can_terminate_environment_instances',
  'can_unblock_environment_instances',
  'can_unregister_environment_instances',
  'list_environment_instance_sessions',
];

const requiredGeneralActionsPermissions: Array<keyof PermissionsData> = [
  'can_activate_environment_instances',
  'can_terminate_environment_instances',
  'can_unblock_environment_instances',
  'can_register_environment_instances',
];

interface Props {
  instancesList: ListInstanceFieldsDataProps;
  environmentSelected: EnvironmentFields | null;
  permissions?: PermissionsData;
  role?: RolesEnum;
  getAllInstancesRequest: ({ id, query }) => void;
  modalOpen: (props: ModalFields) => void;
  modalClose: () => void;
  terminateInstanceRequest: ({ checks, id, instanceUuid }) => void;
  rebootInstanceRequest: ({ environmentId, instanceId }) => void;
  removeInstanceRequest: ({ checks, id }) => void;
  toggleBlockInstancesRequest: ({
    environmentUuid,
    instancesIds,
    action,
  }) => void;
  unlinkInstanceRequest: ({ uuidInstance, id }) => void;
  toggleBlockMultipleInstancesRequest: ({ checks, id, query }) => void;
  activateInstanceRequest: ({ instanceValue, id, query, providerType }) => void;
  connectAsRequest: ({ environmentUuid, instanceId, values }) => void;
  getEnvironmentRequest: ({ id, skipProvider }) => void;
  terminateMultipleInstancesRequest: ({ id, instancesId, query }) => void;
  validateZeroTrustRequest: (props) => void;
}

const InstancesList = ({
  instancesList,
  environmentSelected,
  permissions,
  role,
  getAllInstancesRequest,
  modalOpen,
  modalClose,
  terminateInstanceRequest,
  rebootInstanceRequest,
  toggleBlockInstancesRequest,
  removeInstanceRequest,
  unlinkInstanceRequest,
  activateInstanceRequest,
  connectAsRequest,
  getEnvironmentRequest,
  terminateMultipleInstancesRequest,
  toggleBlockMultipleInstancesRequest,
  validateZeroTrustRequest,
}: Props) => {
  const classes = useStyles();

  const {
    query,
    searchValue,
    itemOrder,
    setQuery,
    handleOrder,
    handleLimitChange,
    handlePageChange,
    handleSearch,
    setSearchValue,
  } = useTableFilters(initialQuery);

  const { checks, handleCheck, handleAllCheck, isChecked } = useCheckboxes<
    InstanceFields,
    string
  >({
    items: instancesList?.results,
    isItemChecked: (item, check) => item?.instance_uuid === check,
    createCheckItem: item => item?.instance_uuid,
  });

  const isAllChecked =
    !!instancesList?.results?.length &&
    instancesList.results.every(item => isChecked(item?.instance_uuid));

  const [update, setUpdate] = React.useState(false);
  const { id, search } = useParams<{ id: string; search: string }>();
  const [openInstanceNumber, setOpenInstanceNumber] = React.useState(false);
  const activeInstancesOptions = Array.from(
    { length: 10 },
    (_, index) => index + 1
  );
  const [timeRequest, setTimeRequest] = React.useState(0);
  const [stopRequest, setStopRequest] = React.useState(true);
  const [refresh, setRefresh] = React.useState(false);
  const [isStatus, setIsStatus] = React.useState({
    status: false,
    instanceId: '',
  });
  const [instanceDetails, setInstanceDetails] = React.useState(false);
  const [instanceSelected, setInstanceSelected] = React.useState<
    InstanceFields
  >();
  const [instanceQueryId] = React.useState(useQuery().get('id'));
  const [environment, setEnvironment] = React.useState<any>();
  const isCreating = environmentSelected?.scale_instances.is_creating ?? false;
  const canUseZeroTrust =
    environmentSelected?.user_connection?.network_type === 'public';

  const intervalRef = React.useRef<any>();
  const RELOAD_TIME = 10000;

  const sectionRef = useRef<HTMLDivElement>(null);

  const is = (key: string, value: any) => {
    return _(environment).get(key) === value;
  };

  const isPeriodOrManual =
    is('behavior.type', 'period') ||
    is('behavior.type', 'manual') ||
    is('behavior.type', 'spot_period');
  const isMicro = is('behavior.type', 'micro');
  const isMulticloud = is('provider.type', 'multicloud');

  const headers: TableCellHeaderProps[] = [
    {
      key: 'instance_id',
      i18n: 'ID',
      order: true,
      hidden: isClientOperator(role),
    },
    {
      key: 'instance_name',
      i18n: 'INSTANCE_KEY',
      order: true,
    },
    {
      key: 'ec2_instance_flavor',
      i18n: 'TYPE_KEY',
      order: true,
      hidden: isMulticloud || isClientOperator(role),
      style: { maxLength: 200 },
    },
    {
      key: 'public_ip',
      i18n: 'PUBLIC_IP_KEY',
      order: true,
      style: { maxLength: 60 },
    },
    {
      key: 'private_ip',
      i18n: 'PRIVATE_IP_KEY',
      hidden: isMulticloud,
      style: { maxLength: 60 },
    },
    {
      key: 'temperature',
      i18n: 'TEMP',
      order: true,
      centerTitle: true,
      hidden: isClientOperator(role),
    },
    {
      key: 'cpu',
      i18n: 'CPU',
      order: true,
      centerTitle: true,
    },
    {
      key: 'memory',
      i18n: 'MEM',
      order: true,
      centerTitle: true,
    },
    {
      key: 'number_of_sessions',
      i18n: 'SES',
      order: true,
      centerTitle: true,
    },
    {
      key: 'status',
      i18n: 'STATUS_KEY',
      order: true,
      centerTitle: true,
      textAlignCenter: true,
    },
    {
      key: 'actions',
      i18n: 'ACTIONS_KEY',
      hidden: !requiredActionsPermissions,
    },
  ].filter(item => !item?.hidden);

  React.useEffect(() => {
    if (isStatus.status) {
      setTimeout(() => {
        setIsStatus({
          instanceId: '',
          status: false,
        });
      }, 15000);
    }
  }, [isStatus]);

  const handleToggleBlockInstances = (
    instancesIds: string[],
    action: 'block' | 'unblock'
  ) => {
    setStopRequest(false);

    toggleBlockInstancesRequest({
      environmentUuid: id,
      instancesIds,
      action,
    });

    setTimeout(() => {
      setUpdate(!update);
    }, 1200);
  };

  const handleTerminateInstanceRequest = (
    checks: Array<string>,
    id: string,
    instanceUuid: string
  ) => {
    setStopRequest(false);
    terminateInstanceRequest({ checks, id, instanceUuid });
    setTimeout(() => {
      setUpdate(!update);
    }, 1200);
  };

  const handleTerminateInstance = (item: InstanceFields) => {
    modalOpen({
      title: i18n.t('WANT_TERMINATE_INSTANCE_KEY'),
      content: i18n.t('WANT_TERMINATE_INSTANCE_DESCRIPTION_KEY', {
        name: item?.instance_name,
      }),
      footer: (
        <Grid>
          <Button
            submitButton
            id="submit-button"
            onClick={() => {
              handleTerminateInstanceRequest(checks, id, item?.instance_uuid);
              modalClose();
            }}
          >
            {i18n.t('TERMINATE_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
    });
  };

  const handleSimulateApp = (item: InstanceFields) =>
    modalOpen({
      overrideClasses: overrideModalSimulate,
      title: i18n.t('TEST_OPEN_KEY'),
      content: <FormSimulateApp instance={item} />,
    });

  const handleTestZeroTrust = (item: InstanceFields) =>
    modalOpen({
      overrideClasses: overrideModalTestZeroTrust,
      title: i18n.t('TEST_ZERO_TRUST_KEY'),
      content: <ValidateZeroTrust instance={item} environmentUuid={id} />,
      footer: (
        <Grid>
          <Button
            submitButton
            id="submit-button"
            onClick={() => {
              validateZeroTrustRequest({
                environmentUuid: id,
                instanceUuid: item?.instance_uuid,
              });
            }}
          >
            {i18n.t('VALIDATE_AGAIN_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CLOSE_KEY')}
          </Button>
        </Grid>
      ),
    });

  const handleRemoveInstance = () => {
    modalOpen({
      title: i18n.t('WANT_REMOVE_INSTANCE_KEY'),
      content: (
        <>
          {checks.length !== 0 ? (
            <Typography className={classes.modalText}>
              {i18n.t('ENVIRONMENTS_PAGE.REMOVE_INSTANCES_WARNING_KEY')}
            </Typography>
          ) : (
            <Typography className={classes.modalText}>
              {i18n.t('ENVIRONMENTS_PAGE.REMOVE_NOT_INSTANCES_WARNING_KEY')}
            </Typography>
          )}
        </>
      ),
      footer: (
        <Grid>
          <Button
            submitButton
            id="submit-button"
            onClick={() => {
              setStopRequest(false);
              removeInstanceRequest({ checks, id });
              modalClose();
            }}
          >
            {i18n.t('REMOVE_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
    });
  };

  const handleBlockInstance = (instanceUuid: string, name: string) => {
    modalOpen({
      title: i18n.t('ENVIRONMENTS_PAGE.WANT_BLOCK_INSTANCE_KEY'),
      content: i18n.t('ENVIRONMENTS_PAGE.BLOCK_INSTANCE_WARNING_KEY', {
        instance: name,
      }),
      footer: (
        <Grid>
          <Button
            submitButton
            id="submit-button"
            onClick={() => {
              handleToggleBlockInstances([instanceUuid], 'block');
              modalClose();
            }}
          >
            {i18n.t('BLOCK_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
    });
  };

  const handleUnblockInstance = (instanceUuid: string, instanceId: string) => {
    modalOpen({
      title: i18n.t('WANT_UNBLOCK_INSTANCE_KEY'),
      content: i18n.t('ENVIRONMENTS_PAGE.UNBLOCK_INSTANCE_WARNING_KEY', {
        instance: instanceId,
      }),
      footer: (
        <Grid>
          <Button
            submitButton
            id="submit-button"
            onClick={() => {
              handleToggleBlockInstances([instanceUuid], 'unblock');
              modalClose();
            }}
          >
            {i18n.t('UNBLOCK_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
    });
  };

  const handleUnlinkInstanceRequest = (uuidInstance: string) => {
    setStopRequest(false);
    unlinkInstanceRequest({ uuidInstance, id });
  };

  const handleUnlinkInstance = (uuidInstance: string, instanceId: string) => {
    modalOpen({
      title: i18n.t('ENVIRONMENTS_PAGE.WISH_UNLINK_INSTANCE_KEY'),
      content: i18n.t('ENVIRONMENTS_PAGE.UNLINK_WARNING_KEY', {
        instance: instanceId,
      }),
      maxWidth: 'sm',
      footer: (
        <Grid>
          <Button
            submitButton
            id="submit-button"
            onClick={() => {
              handleUnlinkInstanceRequest(uuidInstance);
              modalClose();
            }}
          >
            {i18n.t('UNLINK_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
    });
  };

  const handleRebootInstance = (
    environmentId: string,
    item: InstanceFields
  ) => {
    modalOpen({
      title: i18n.t('WANT_REBOOT_INSTANCE_KEY'),
      content: i18n.t('WANT_REBOOT_INSTANCE_DESCRIPTION_KEY', {
        name: item?.instance_name,
      }),
      maxWidth: 'sm',
      footer: (
        <Grid>
          <Button
            submitButton
            id="submit-button"
            onClick={() => {
              setStopRequest(false);
              rebootInstanceRequest({
                environmentId,
                instanceId: item?.instance_uuid,
              });
              setTimeout(() => {
                setUpdate(!update);
              }, 1200);
              setIsStatus({
                status: true,
                instanceId: item?.instance_id,
              });
              modalClose();
            }}
          >
            {i18n.t('REINITIALIZE_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
    });
  };

  const handleTerminateMultipleInstance = () => {
    modalOpen({
      title: i18n.t('TERMINATE_INSTANCES_KEY'),
      content: i18n.t('ENVIRONMENTS_PAGE.TERMINATE_MULTIPLE_INSTANCES_MSG_KEY'),
      footer: (
        <Grid>
          <Button
            submitButton
            id="submit-button"
            onClick={() => {
              terminateMultipleInstancesRequest({
                id,
                query,
                instancesId: checks,
              });
              modalClose();
            }}
          >
            {i18n.t('TERMINATE_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
    });
  };

  const handleToggleBlockMultipleInstances = () => {
    modalOpen({
      title: i18n.t('TOGGLE_BLOCK_UNBLOCK_INSTANCE_KEY'),
      content: i18n.t('TOGGLE_BLOCK_UNBLOCK_INSTANCE_MSG_KEY'),
      footer: (
        <Grid>
          <Button
            submitButton
            id="submit-button"
            onClick={() => {
              toggleBlockMultipleInstancesRequest({ checks, id, query });
              setStopRequest(false);
              setTimeout(() => {
                setUpdate(!update);
              }, 1200);
              modalClose();
            }}
          >
            {i18n.t('CONFIRM_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
    });
  };

  const handleActiveInstance = (instanceValue: number) => {
    setStopRequest(false);
    activateInstanceRequest({
      instanceValue,
      id,
      query,
      providerType: environmentSelected?.provider.type,
    });
  };

  const handleConnect = (instanceId: string, username: string) => {
    modalOpen({
      title: i18n.t('CONNECT_KEY'),
      maxWidth: 'xs',
      content: (
        <FormConnectAs
          onCancel={modalClose}
          onSubmit={({ password }) => {
            connectAsRequest({
              environmentUuid: environment?.environment_uuid,
              instanceId,
              values: {
                ad_username: username,
                operator_password: password,
              },
            });
          }}
        />
      ),
    });
  };

  const getInstances = (refresh = false) => {
    getAllInstancesRequest({ id, query });
    setRefresh(refresh);
  };

  React.useEffect(() => {
    getInstances();
  }, [getAllInstancesRequest, id, query, timeRequest, update]);

  const scrollToSection = () => {
    if (sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  React.useEffect(() => {
    const selectedInstance = instancesList?.results?.find(
      instance => instance?.instance_uuid === instanceSelected?.instance_uuid
    );

    if (selectedInstance) {
      setInstanceSelected(selectedInstance);
      scrollToSection();
    }

    setInstanceDetails(!instanceDetails);
  }, [update, instancesList]);

  React.useEffect(() => {
    setEnvironment(environmentSelected);
  }, [environmentSelected]);

  React.useEffect(() => {
    intervalRef.current = setInterval(() => {
      setTimeRequest(props => props + 1);
    }, RELOAD_TIME);
    return () => clearInterval(intervalRef.current);
  }, []);

  React.useEffect(() => {
    if (instanceQueryId) {
      setQuery({
        ...initialQuery,
        search: instanceQueryId,
      });
    }
  }, [instanceQueryId]);

  React.useEffect(() => {
    if (search) {
      setQuery({
        ...initialQuery,
        search,
      });
    }
  }, [search]);

  React.useEffect(() => {
    const stop = instancesList?.results?.find(item => {
      return item && !['OPERACIONAL', 'INACESSIVEL'].includes(item?.status);
    });

    setStopRequest(stop === undefined);
  }, [instancesList]);

  React.useEffect(() => {
    setStopRequest(!isCreating);
  }, [getEnvironmentRequest, environmentSelected]);

  React.useEffect(() => {
    getEnvironmentRequest({ id, skipProvider: true });
  }, []);

  const handleGetDetails = item => {
    setInstanceDetails(!instanceDetails);
    setInstanceSelected(item);
    scrollToSection();
  };

  React.useEffect(() => {
    if (stopRequest) {
      clearInterval(intervalRef.current);
    } else {
      intervalRef.current = setInterval(() => {
        setTimeRequest(props => props + 1);
      }, RELOAD_TIME);
    }
  }, [stopRequest]);

  const hasPermissionToActiveInstance = () => {
    return !isMulticloud && isPeriodOrManual
      ? true
      : !!permissions && !permissions.can_register_environment_instances;
  };

  return (
    <>
      <EnvironmentsTemplate
        currentPage="instances"
        titlePage={environment?.name}
        breadcrumbs={[
          {
            name: i18n.t('ENVIRONMENTS_KEY'),
            link: '/environments',
          },
          {
            name: i18n.t('INSTANCES_KEY'),
          },
        ]}
        titleContent={`${i18n.t(
          'ENVIRONMENTS_PAGE.LIST_INSTANCES_TITLE_KEY'
        )} - ${environmentStatus(
          environmentSelected?.behavior?.type || `${i18n.t('LOADING_KEY')}...`
        )}`}
        subtitleContent={i18n.t(
          'ENVIRONMENTS_PAGE.LIST_INSTANCES_SUBTITLE_KEY'
        )}
        actionsContent={
          <Grid
            container
            direction="row"
            alignItems="center"
            justify="flex-end"
            xs={6}
          >
            <RefreshButton
              id="btn-refresh"
              onClick={() => {
                getInstances(true);
              }}
              animate={instancesList?.loading}
              disabled={instancesList?.loading}
            />

            {!isMicro && (
              <Permission value={requiredGeneralActionsPermissions}>
                <ButtonGroup
                  style={{ marginLeft: 16 }}
                  label={i18n.t('GENERAL_ACTIONS_KEY')}
                  options={[
                    {
                      label: i18n.t('ACTIVE_INSTANCE_KEY'),
                      onClick: () => setOpenInstanceNumber(!openInstanceNumber),
                      hidden: isMulticloud
                        ? true
                        : !!permissions &&
                          !permissions.can_activate_environment_instances,
                      icon: (
                        <KeyboardArrowRightIcon
                          className={classes.keyboardIcon}
                        />
                      ),
                      subOptions: activeInstancesOptions.map(option => ({
                        id: `option-active-instance-${option}`,
                        label: `${option} ${i18n.t('INSTANCE_KEY')}`,
                        onClick: () => handleActiveInstance(option),
                      })),
                    },
                    {
                      label: i18n.t('TERMINATE_INSTANCE_KEY'),
                      hidden: isMulticloud
                        ? true
                        : !!permissions &&
                          !permissions.can_terminate_environment_instances,
                      disabled: checks.length === 0,
                      onClick: handleTerminateMultipleInstance,
                    },
                    {
                      label: i18n.t('TOGGLE_BLOCK_UNBLOCK_INSTANCE_KEY'),
                      hidden: isMulticloud
                        ? true
                        : !!permissions &&
                          !permissions.can_unblock_environment_instances,
                      disabled: checks.length === 0,
                      onClick: handleToggleBlockMultipleInstances,
                    },
                    {
                      label: i18n.t('REGISTER_INSTANCE_KEY'),
                      hidden: hasPermissionToActiveInstance(),
                      onClick: () =>
                        modalOpen({
                          title: i18n.t('REGISTER_INSTANCE_KEY'),
                          content: (
                            <FormRegisterInstance
                              environmentUuid={id}
                              onCancel={modalClose}
                            />
                          ),
                        }),
                    },
                    {
                      label: i18n.t('REMOVE_INSTANCE_KEY'),
                      hidden: !!isMulticloud || isPeriodOrManual,
                      onClick: handleRemoveInstance,
                    },
                  ]}
                />
              </Permission>
            )}
          </Grid>
        }
      >
        <Grid container className={classes.table}>
          <Grid item xs={6}>
            <SearchBar
              id="search-instances"
              isLoading={instancesList?.loading}
              searchValue={searchValue}
              defaultValue={instanceQueryId || search}
              handleSearch={handleSearch}
              setSearchValue={setSearchValue}
            />
          </Grid>
          {environmentSelected?.scale_instances.is_creating && (
            <Grid
              container
              xs={6}
              direction="row"
              alignItems="center"
              justify="flex-end"
              className={classes.initializingMsg}
            >
              <CircularProgress size={16} color="secondary" />
              <Typography>
                {getInitializingInstancesMessage(environmentSelected)}
              </Typography>
            </Grid>
          )}

          <TableItems
            overrideRowsCount={25}
            header={
              <>
                <Permission value={requiredGeneralActionsPermissions}>
                  <TableCell className={classes.checkboxCell}>
                    <Checkbox
                      checked={isAllChecked}
                      onClick={() => handleAllCheck(isAllChecked)}
                    />
                  </TableCell>
                </Permission>
                {headers.map(item => (
                  <TableCell
                    key={item?.i18n}
                    order={item?.order}
                    style={item?.style}
                    hidden={item?.hidden}
                    centerTitle={item?.centerTitle}
                    textAlignCenter={item?.textAlignCenter}
                    markOderItem={itemOrder === item?.key}
                    handleOrder={() =>
                      item?.order ? handleOrder(item?.key) : null
                    }
                  >
                    {i18n.t(item?.i18n)}
                  </TableCell>
                ))}
              </>
            }
            renderItems={(item: InstanceFields, index) => (
              <TableRow
                key={item?.instance_uuid}
                style={{
                  backgroundColor: checks.find(
                    check => check === item?.instance_uuid
                  )
                    ? palette.checkedLightYellow
                    : palette.transparent,
                }}
              >
                <Permission value={requiredGeneralActionsPermissions}>
                  <TableCell className={classes.checkboxCell}>
                    <Checkbox
                      onClick={() => handleCheck(item?.instance_uuid)}
                      checked={isChecked(item?.instance_uuid)}
                    />
                  </TableCell>
                </Permission>
                {!isClientOperator(role) && (
                  <TableCell
                    ellipsis
                    copyable
                    maxLength={10}
                    className={classes.tableCellInstanceId}
                  >
                    {item?.instance_id}
                  </TableCell>
                )}
                <TableCell ellipsis className={classes.instanceCell}>
                  <Tooltip title={item?.instance_name} arrow>
                    <Typography className={classes.instanceCellName}>
                      {item?.instance_name}
                    </Typography>
                  </Tooltip>
                  <Grid>
                    {item?.status_details?.sessions_timeout_alert && (
                      <Tooltip
                        arrow
                        title={i18n.t('MULTIPLE_EXPIRED_SESSIONS')}
                        className={classes.tooltip}
                      >
                        <TimerOff
                          className={classes.sessionsTimeoutAlertIcon}
                        />
                      </Tooltip>
                    )}
                    <ZeroTrustIcon status={item?.zero_trust?.state} />
                    {item?.cpu_benchmark && !isClientOperator(role) && (
                      <BenchmarkIcon
                        value={item?.cpu_benchmark?.result ?? null}
                        state={item?.cpu_benchmark?.state ?? 'disabled'}
                        finishedAt={item?.cpu_benchmark?.finished_at ?? null}
                        executionIntervalMinutes={
                          item?.cpu_benchmark?.execution_interval_minutes ??
                          null
                        }
                        durationSeconds={
                          item?.cpu_benchmark?.duration_seconds ?? null
                        }
                      />
                    )}

                    <Tooltip
                      title={
                        <InstanceDetailsTooltip
                          instance={item}
                          classes={classes}
                        />
                      }
                      classes={{ tooltip: classes.noMaxWidth }}
                      className={classes.tooltip}
                      arrow
                    >
                      <InfoOutlinedIcon
                        className={classes.accessTimeIcon}
                        onClick={() => {
                          handleGetDetails(item);
                          getInstances(true);
                        }}
                      />
                    </Tooltip>
                  </Grid>
                </TableCell>
                {!is('provider.type', 'multicloud') && !isClientOperator(role) && (
                  <TableCell
                    tooltip={item?.flavor}
                    className={classes.tableCellFlavor}
                  >
                    <span
                      className={
                        item?.is_spot ? classes.isSpot : classes.flagType
                      }
                    >
                      {item?.flavor}
                    </span>
                  </TableCell>
                )}
                <TableCell
                  copyable
                  tooltip={item?.public_ip}
                  className={classes.text}
                >
                  {item?.public_ip || (
                    <CircularProgress size={16} color="secondary" />
                  )}
                </TableCell>
                {is('provider.type', 'multicloud') &&
                is('behavior.type', 'manual') ? null : (
                  <TableCell className={classes.text}>
                    {item?.private_ip || (
                      <CircularProgress size={16} color="secondary" />
                    )}
                  </TableCell>
                )}
                {!isClientOperator(role) && (
                  <TableCell className={classes.metrics}>
                    <PercentIndicator
                      isEnv
                      value={item?.temperature}
                      isCapacity
                    />
                  </TableCell>
                )}
                <TableCell className={classes.metrics}>
                  <PercentIndicator isEnv value={item?.cpu} />
                </TableCell>
                <TableCell className={classes.metrics}>
                  <PercentIndicator isEnv value={item?.memory} />
                </TableCell>
                <TableCell
                  className={getMonitorCellStyle(
                    item,
                    'number_of_sessions',
                    classes,
                    item?.number_max_of_sessions
                  )}
                >
                  {item?.number_of_sessions}
                  {!isClientOperator(role) &&
                    `/${item?.number_max_of_sessions}`}
                </TableCell>
                <TableCell
                  centerTitle
                  tooltip={item?.status}
                  className={classes.statusCell}
                >
                  <StatusInstance
                    value={
                      isRefreshStatusReboot(
                        isStatus.status,
                        item?.instance_id,
                        isStatus.instanceId
                      )
                        ? 'INICIALIZANDO'
                        : item?.status
                    }
                  />
                </TableCell>
                <Permission value={requiredActionsPermissions}>
                  <TableCell
                    textAlignCenter
                    className={classes.tableCellMenuOptions}
                    centerTitle
                  >
                    <MenuActions
                      id={`btn-options-instances-${index}`}
                      disabled={
                        isRefreshStatusReboot(
                          isStatus.status,
                          item?.instance_id,
                          isStatus.instanceId
                        )
                          ? true
                          : ![
                              'OPERACIONAL',
                              'PARADA',
                              'INACESSIVEL',
                              'BLOQUEADA',
                              'CHEIA',
                              'INICIALIZANDO',
                              'LIGADA',
                            ].includes(item?.status)
                      }
                    >
                      {getMenuActionItems(
                        item,
                        id,
                        environmentSelected,
                        isPeriodOrManual,
                        isMulticloud,
                        canUseZeroTrust,
                        modalClose,
                        modalOpen,
                        handleRebootInstance,
                        handleUnlinkInstance,
                        handleUnblockInstance,
                        handleBlockInstance,
                        handleTestZeroTrust,
                        handleTerminateInstance,
                        handleSimulateApp,
                        handleConnect,
                        permissions,
                        role
                      )?.length > 0 ? (
                        getMenuActionItems(
                          item,
                          id,
                          environmentSelected,
                          isPeriodOrManual,
                          isMulticloud,
                          canUseZeroTrust,
                          modalClose,
                          modalOpen,
                          handleRebootInstance,
                          handleUnlinkInstance,
                          handleUnblockInstance,
                          handleBlockInstance,
                          handleTestZeroTrust,
                          handleTerminateInstance,
                          handleSimulateApp,
                          handleConnect,
                          permissions,
                          role
                        )?.map(action => (
                          <MenuActionItem
                            key={action?.key}
                            id={action?.id ?? action?.key}
                            onClick={action?.onClick}
                            style={action?.style}
                          >
                            {action?.label}
                          </MenuActionItem>
                        ))
                      ) : (
                        <MenuActionItem>
                          {i18n.t('NO_ACTIONS_AVAILABLE_KEY')}
                        </MenuActionItem>
                      )}
                    </MenuActions>
                  </TableCell>
                </Permission>
              </TableRow>
            )}
            data={instancesList}
            isLoading={!refresh && stopRequest && instancesList?.loading}
            onChangePage={handlePageChange}
            skeletonLoading
            onChangeLimit={handleLimitChange}
            notFoundText={i18n.t('ENVIRONMENTS_PAGE.NO_INSTANCE_KEY')}
            colCount={12}
          />
        </Grid>
      </EnvironmentsTemplate>
      <Grid container innerRef={sectionRef} className={classes.bottomPage}>
        {instanceSelected && (
          <InstanceDetails
            instanceSelected={instanceSelected}
            isMulticloud={isMulticloud}
            isPeriodOrManual={isPeriodOrManual}
            handleTestZeroTrust={handleTestZeroTrust}
            handleSimulateApp={handleSimulateApp}
            handleTerminateInstance={handleTerminateInstance}
            handleRebootInstance={handleRebootInstance}
            handleUnblockInstance={handleUnblockInstance}
            handleBlockInstance={handleBlockInstance}
            setInstanceSelected={setInstanceSelected}
            handleConnect={handleConnect}
          />
        )}
      </Grid>
    </>
  );
};

const mapStateToProps = ({
  authenticate: { data },
  environments: { instancesList, environmentSelected },
}: ApplicationState) => ({
  instancesList,
  permissions: data?.permissions,
  role: data?.role,
  environmentSelected,
});

export default connect(mapStateToProps, {
  getAllInstancesRequest,
  getEnvironmentRequest,
  modalOpen,
  modalClose,
  terminateInstanceRequest,
  rebootInstanceRequest,
  toggleBlockInstancesRequest,
  removeInstanceRequest,
  unlinkInstanceRequest,
  activateInstanceRequest,
  connectAsRequest,
  terminateMultipleInstancesRequest,
  toggleBlockMultipleInstancesRequest,
  validateZeroTrustRequest,
})(InstancesList);
